import { ROUTES } from '../../utils/constants';
import type { ExpositoryWritingPracticeSlice } from './api/malayFormativeProgress.client';
import { buildMalayAiTutorLiveUrl } from './malayAiTutorActivityRoute';

export const EXPOSITORY_TOPIC_ID = 'penulisan_ekspositori';

export type ExpositoryWritingCta = {
  visible: boolean;
  topicId: string;
  route: string;
  status: string;
  statusLabel: string;
  primaryLabel: string | null;
  secondaryLabel: string | null;
};

/** Teaching percent must not gate writing — normalize legacy locked/missing to ready. */
export function normalizeExpositoryWritingStatus(status: string | undefined | null): string {
  if (!status || status === 'locked') return 'ready';
  return status;
}

export function buildExpositoryWritingStudioUrl(
  topicId: string = EXPOSITORY_TOPIC_ID,
): string {
  return `${ROUTES.STUDENT_MALAY_EXPOSITORY_WRITING}/${encodeURIComponent(topicId)}`;
}

export function buildExpositoryWritingBackUrl(
  topicId: string = EXPOSITORY_TOPIC_ID,
): string {
  return buildMalayAiTutorLiveUrl('malay_unit_5', { topicId });
}

export function expositoryWritingPracticeLabel(status: string | undefined): string {
  switch (status) {
    case 'locked':
      return '🔒 Selesaikan pembelajaran dahulu';
    case 'ready':
      return 'Ready';
    case 'draft':
      return 'Draft saved';
    case 'submitted':
      return 'Submitted';
    case 'reviewed':
      return 'Reviewed';
    default:
      return status || 'Not started';
  }
}

export function expositoryPrimaryCtaLabel(status: string | undefined): string | null {
  switch (status) {
    case 'ready':
      return 'Write Expository';
    case 'draft':
      return 'Continue Writing';
    case 'reviewed':
    case 'submitted':
      return 'View Writing Feedback';
    default:
      return null;
  }
}

export function expositorySecondaryCtaLabel(status: string | undefined): string | null {
  if (status === 'reviewed' || status === 'submitted') {
    return 'Revise';
  }
  return null;
}

/** Shared TOC + main Tutor control CTA for Penulisan Ekspositori Writing Studio. */
export function resolveExpositoryWritingCta(input: {
  topicId: string | null | undefined;
  writingPractice?: ExpositoryWritingPracticeSlice | null;
  authorized?: boolean;
}): ExpositoryWritingCta {
  const empty: ExpositoryWritingCta = {
    visible: false,
    topicId: '',
    route: '',
    status: '',
    statusLabel: '',
    primaryLabel: null,
    secondaryLabel: null,
  };
  const topicId = String(input.topicId || '').trim();
  if (topicId !== EXPOSITORY_TOPIC_ID) return empty;
  if (input.authorized === false) return empty;

  const status = normalizeExpositoryWritingStatus(input.writingPractice?.status);
  return {
    visible: true,
    topicId,
    route: buildExpositoryWritingStudioUrl(topicId),
    status,
    statusLabel: expositoryWritingPracticeLabel(status),
    primaryLabel: expositoryPrimaryCtaLabel(status),
    secondaryLabel: expositorySecondaryCtaLabel(status),
  };
}
